/**
 * The browser side of the owner-only `admin` function (T066, contracts/admin-function.md).
 *
 * Every request carries the signed-in person's access token, which the function checks itself.
 * Every refusal or failure comes back as the French message the people screen shows, so no screen
 * has to know the function's reason codes. The transport (a signed Lambda invoke in production,
 * a fake on previews) is injected.
 */
import { t, type MessageKey } from '../i18n';

export interface AdminPerson {
  /** Also the person's cellar id. */
  id: string;
  email: string;
  role: 'owner' | 'member';
  /** Whether the person can currently sign in. */
  enabled: boolean;
}

/** Sends one request to the function and returns its parsed JSON answer. */
export type AdminTransport = (request: Record<string, unknown>) => Promise<unknown>;

export type AdminResult<T> = { ok: true; value: T } | { ok: false; message: string };

export interface AdminClient {
  listPeople(): Promise<AdminResult<AdminPerson[]>>;
  addPerson(email: string): Promise<AdminResult<AdminPerson>>;
  setEnabled(id: string, enabled: boolean): Promise<AdminResult<AdminPerson>>;
}

const MESSAGE_FOR_REASON: Record<string, MessageKey> = {
  limit_reached: 'admin.limitReached',
  already_exists: 'admin.alreadyExists',
  cannot_disable_owner: 'admin.cannotDisableOwner',
  invalid_email: 'admin.invalidEmail',
  not_found: 'admin.notFound',
  forbidden: 'admin.forbidden',
};

function isPerson(value: unknown): value is AdminPerson {
  const p = value as Partial<AdminPerson> | null;
  return (
    typeof p === 'object' &&
    p !== null &&
    typeof p.id === 'string' &&
    typeof p.email === 'string' &&
    (p.role === 'owner' || p.role === 'member') &&
    typeof p.enabled === 'boolean'
  );
}

const failure = (key: MessageKey): { ok: false; message: string } => ({
  ok: false,
  message: t(key),
});

export function createAdminClient(
  transport: AdminTransport,
  getAccessToken: () => Promise<string>,
): AdminClient {
  async function call<T>(
    request: Record<string, unknown>,
    read: (answer: Record<string, unknown>) => T | undefined,
  ): Promise<AdminResult<T>> {
    let answer: unknown;
    try {
      answer = await transport({ ...request, accessToken: await getAccessToken() });
    } catch {
      return failure('admin.network');
    }
    if (typeof answer !== 'object' || answer === null) return failure('admin.unavailable');
    const a = answer as Record<string, unknown>;
    if (a.ok === false) {
      const key = typeof a.reason === 'string' ? MESSAGE_FOR_REASON[a.reason] : undefined;
      return failure(key ?? 'admin.unavailable');
    }
    const value = a.ok === true ? read(a) : undefined;
    return value === undefined ? failure('admin.unavailable') : { ok: true, value };
  }

  const person = (a: Record<string, unknown>) => (isPerson(a.person) ? a.person : undefined);

  return {
    listPeople: () =>
      call({ op: 'listPeople' }, (a) =>
        Array.isArray(a.people) && a.people.every(isPerson) ? a.people : undefined,
      ),
    addPerson: (email) => call({ op: 'addPerson', email }, person),
    setEnabled: (id, enabled) => call({ op: 'setEnabled', id, enabled }, person),
  };
}
